import type { typePoolTab } from "../poolScreen/poolScreenRoute";

/**
 * Turns one inbox item's `deepLink` into the `{ to, search }` a TanStack
 * `navigate` call takes. Returns data only, the caller navigates.
 * A waiting for approval alert opens the pool's Members tab, because that is
 * where the Requests section is.
 * A pick reminder opens the pool's Game tab on the round it names, or the Game
 * tab alone when the row predates the round (ISSUE-1176).
 * A Strip Card alert opens the pool's Game tab on the game it names
 * (ISSUE-1295).
 * A follow alert opens the Followers screen on the tab it is about: a request
 * on Requests, a new follower on Followers, an approval on Following
 * (ISSUE-1374).
 */
export type typeInboxDeepLink = {
  kind:
    "pool" | "invite" | "game" | "chat" | "team" | "stripCard" | "followers";
  poolId?: string | null;
  inviteId?: string | null;
  eventId?: string | null;
  chatId?: string | null;
  teamId?: string | null;
  roundId?: string | null;
  /** ISSUE-1390. The game detail tab a game link opens, "plays" for a
   *  mid-game update; absent on older rows. */
  tab?: string | null;
};

export type typeInboxDestination = {
  to:
    | "/pools"
    | "/pools/invitations"
    | "/messages"
    | "/scores"
    | "/account/followers";
  search: Record<string, string>;
};

export const MEMBERS_TAB_ALERT_KIND = "memberWaitingApproval";
export const GAME_TAB_ALERT_KIND = "pickReminder";
const MEMBERS_TAB: typePoolTab = "members";
const GAME_TAB: typePoolTab = "game";
const FOLLOWERS_TAB_BY_ALERT_KIND: Record<string, string> = {
  fanFollowRequest: "requests",
  fanFollowNew: "followers",
  fanFollowAccepted: "following",
};

export function resolveInboxDestination({
  deepLink,
  alertKind,
}: {
  deepLink: typeInboxDeepLink;
  alertKind?: string | undefined;
}): typeInboxDestination {
  switch (deepLink.kind) {
    case "pool":
      if (!deepLink.poolId) {
        return { to: "/pools", search: {} };
      }
      if (alertKind === MEMBERS_TAB_ALERT_KIND) {
        return {
          to: "/pools",
          search: { poolId: deepLink.poolId, tab: MEMBERS_TAB },
        };
      }
      if (alertKind === GAME_TAB_ALERT_KIND) {
        const search: Record<string, string> = {
          poolId: deepLink.poolId,
          tab: GAME_TAB,
        };
        if (typeof deepLink.roundId === "string" && deepLink.roundId !== "") {
          search.roundId = deepLink.roundId;
        }
        return { to: "/pools", search };
      }
      return { to: "/pools", search: { poolId: deepLink.poolId } };
    case "stripCard": {
      if (!deepLink.poolId) {
        return { to: "/pools", search: {} };
      }
      const search: Record<string, string> = {
        poolId: deepLink.poolId,
        tab: GAME_TAB,
      };
      if (typeof deepLink.eventId === "string" && deepLink.eventId !== "") {
        search.eventId = deepLink.eventId;
      }
      return { to: "/pools", search };
    }
    case "invite":
      return {
        to: "/pools/invitations",
        search: deepLink.inviteId ? { inviteId: deepLink.inviteId } : {},
      };
    case "chat":
      return {
        to: "/messages",
        search: deepLink.chatId ? { conversationId: deepLink.chatId } : {},
      };
    case "game": {
      if (!deepLink.eventId) {
        return { to: "/scores", search: {} };
      }
      // The game screen drops a tab it does not offer, so the server's word
      // is passed through as sent.
      const tab = typeof deepLink.tab === "string" ? deepLink.tab : "";
      return {
        to: "/scores",
        search:
          tab === ""
            ? { eventId: deepLink.eventId }
            : { eventId: deepLink.eventId, tab },
      };
    }
    case "team":
      return {
        to: "/scores",
        search: deepLink.teamId ? { teamId: deepLink.teamId } : {},
      };
    case "followers": {
      const tab =
        alertKind === undefined
          ? undefined
          : FOLLOWERS_TAB_BY_ALERT_KIND[alertKind];
      return {
        to: "/account/followers",
        search: tab === undefined ? {} : { tab },
      };
    }
    default:
      return { to: "/pools", search: {} };
  }
}
